import { openDB } from 'idb';
import { GeneratePushID } from '../../../modules/GeneratePushID';

const QUEUE_STORE = 'queue';

const db = await openDB('photo_uploads', 1, {
  upgrade(db) {
    const store = db.createObjectStore(QUEUE_STORE);
    store.createIndex('jobId', 'jobId');
  }
});

/** @typedef {Object} NodeAssociationConfig
 * @property {string} nodeId
 */

/** @typedef {Object} SectionAssociationConfig
 * @property {string} connectionId
 * @property {string} sectionId
 */

/** @typedef {NodeAssociationConfig | SectionAssociationConfig} AssociationConfig */

/**
 * Metadata to store with the photo upload.
 * @typedef {Record<string, any>} Metadata
 */

/**
 * An update to apply to ...{jobId}/photos/{photoId} after upload.
 * @typedef {Record<string, any>} PhotoUpdate
 */

/**
 * An update object to apply to ...{jobId}/nodes/{nodeId}/photos/{photoId} or ...{jobId}/connections/{connectionId}/sections/{sectionId}/photos/{photoId} after upload.
 * @typedef {Record<string, any>} AssociatedPhotoUpdate
 */

/** @typedef {Object} QueueRecordData
 *  @property {string} jobId
 *  @property {string} filetype
 *  @property {string} filename
 *  @property {string} uploadedBy
 *  @property {Metadata} [metadata]
 *  @property {PhotoUpdate} [photoUpdate]
 *  @property {AssociationConfig} [association]
 *  @property {AssociatedPhotoUpdate} [associatedPhotoUpdate]
 */

/** @typedef {QueueRecordData & { id: string }} QueueRecord */

const QUEUE_RECORD_PROPERTIES = {
  jobId: { type: 'string' },
  filetype: { type: 'string' },
  filename: { type: 'string' },
  uploadedBy: { type: 'string' },
  metadata: { type: 'object', optional: true },
  association: { type: 'object', optional: true },
  associatedPhotoUpdate: { type: 'object', optional: true },
  photoUpdate: { type: 'object', optional: true }
};

/**
 * Validates that a queue record has the required properties and types.
 * @param {QueueRecordData} queueRecordData
 * @throws {Error} If the queue record is missing required properties or has invalid types.
 */
function validateQueueRecordData(queueRecordData) {
  for (const [prop, { type, optional }] of Object.entries(QUEUE_RECORD_PROPERTIES)) {
    const propertyExists = queueRecordData[prop] != null;
    if (!propertyExists && !optional) {
      throw new Error(`Queue record is missing required property: ${prop}`);
    } else if (propertyExists && typeof queueRecordData[prop] !== type) {
      throw new Error(`Queue record property ${prop} must be of type ${type}`);
    }
  }
}

/**
 * Adds a photo upload record to the queue store.
 * @param {QueueRecordData} queueRecordData
 * @returns {Promise<string>} The id of the added queue record.
 */
async function addQueueRecord(queueRecordData) {
  validateQueueRecordData(queueRecordData);
  const recordId = GeneratePushID();
  await db.add(QUEUE_STORE, queueRecordData, recordId);
  return recordId;
}

/**
 * Removes a photo upload record from the queue store.
 * @param {string} recordId
 * @return {Promise<void>}
 */
async function removeQueueRecord(recordId) {
  await db.delete(QUEUE_STORE, recordId);
}

/**
 * Retrieves a photo upload record from the queue store.
 * @param {string} recordId
 * @returns {Promise<QueueRecord>} The retrieved queue record.
 */
async function getQueueRecord(recordId) {
  /** @type {QueueRecordData} */
  const queueRecordData = await db.get(QUEUE_STORE, recordId);
  return { ...queueRecordData, id: recordId };
}

/**
 * Retrieves all photo upload record ids from the queue store for a specific job.
 * @param {string} jobId
 * @returns {Promise<QueueRecord[]>} The retrieved queue records.
 */
async function getQueueRecordsForJob(jobId) {
  const index = db.transaction(QUEUE_STORE).store.index('jobId');
  /** @type {QueueRecord[]} */
  const queueRecords = [];
  for await (const cursor of index.iterate(jobId)) {
    const queueRecordData = /** @type {QueueRecordData} */ (cursor.value);
    const queueRecordId = /** @type {string} */ (cursor.primaryKey);
    const queueRecord = { ...queueRecordData, id: queueRecordId };
    queueRecords.push(queueRecord);
  }
  return queueRecords;
}

/**
 * Retrieves all photo upload record ids from the queue store for a specific job and node.
 * @param {string} jobId
 * @param {string} nodeId
 * @returns {Promise<QueueRecord[]>} The retrieved queue records.
 */
async function getQueueRecordsForNode(jobId, nodeId) {
  const jobRecords = await getQueueRecordsForJob(jobId);
  const nodeRecords = jobRecords.filter((record) => {
    return record.association != null && 'nodeId' in record.association && record.association.nodeId === nodeId;
  });
  return nodeRecords;
}

export { addQueueRecord, removeQueueRecord, getQueueRecord, getQueueRecordsForJob, getQueueRecordsForNode };
